/* ==========================================================================
   Terlato Kitchen — "Find Their Gift" · the match
   sections/gf-finder-result.liquid

   This file owns the matching. It reads the rules the section rendered out of
   the `gift_finder_match` metaobject, watches window.GFFinder for a completed
   set of answers, and paints the winning product into the band.

   HOW A WINNER IS CHOSEN
   A rule is a set of three lists — recipient, occasion, comfort_level. A list
   that names the visitor's answer passes; an EMPTY list is a wildcard and also
   passes; a list that names other answers eliminates the rule outright.

   Among the survivors the most SPECIFIC wins: score is simply how many of the
   three lists were real filters rather than wildcards, so a rule pinning all
   three (score 3) always beats one pinning two (score 2), whatever their
   priorities. `priority` breaks a tie between rules of equal specificity, and
   document order breaks a tie between those.

   That ordering is what makes a catch-all entry safe. Leave all three lists
   empty and it scores 0 — it can never displace a real rule, and it means a
   visitor is never told there is nothing for them.

   NOTHING IS BUILT FROM A STRING. Cards are cloned from a <template> and filled
   with textContent and attributes, so a product title carrying a quote or an
   angle bracket stays text and never becomes markup.
   ========================================================================== */

(function (window, document) {
  'use strict';

  /* The three metaobject list fields, in the order they are scored and in the
     order the picks pill reads them. These are the metaobject's schema — a
     fourth question needs a fourth field in Shopify admin first. */
  var MATCH_KEYS = ['recipient', 'occasion', 'comfort_level'];

  var SEL = {
    root: '[data-gf-finder-result]',
    rules: '[data-gf-finder-rules]',
    template: '[data-gf-card-template]',
    cards: '[data-gf-result-cards]',
    alts: '[data-gf-result-alts]',
    altsGrid: '[data-gf-result-alts-grid]',
    altTemplate: '[data-gf-alt-template]',
    empty: '[data-gf-result-empty]',
    live: '[data-gf-result-live]'
  };

  function each(list, fn) {
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  /* Problems are always reported. The step-by-step trace is opt-in via
     ?gf-debug=1, so a working page stays quiet in the console. */
  function warn(message) {
    if (window.console && window.console.warn) window.console.warn('[gf-finder] ' + message);
  }

  function debug() {
    if (window.location.search.indexOf('gf-debug=1') === -1) return;
    if (!window.console || !window.console.log) return;
    var args = ['[gf-finder]'].concat([].slice.call(arguments));
    window.console.log.apply(window.console, args);
  }

  function summarise(answers) {
    var parts = [];
    for (var key in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, key)) {
        parts.push(key + '=' + answers[key].value);
      }
    }
    return parts.length ? parts.join(', ') : '(none)';
  }

  function fill(scope, name, text) {
    var el = scope.querySelector('[data-gf-card-' + name + ']');
    if (!el) return null;
    if (text === null || text === undefined || text === '') {
      el.hidden = true;
      el.textContent = '';
    } else {
      el.hidden = false;
      el.textContent = text;
    }
    return el;
  }

  /* ---------------------------------------------------------------------
     Matching
     --------------------------------------------------------------------- */

  /* The three answer fields are meant to be `list.single_line_text_field`, which
     reaches us as an array. A field defined as a SINGLE value instead arrives as
     a bare string, and a bare string would still answer `.indexOf()` — turning
     the test into substring matching, where `for-her` quietly satisfies a rule
     that only named `for-her-and-them`. Normalising both shapes to an array
     closes that, and lets a single-value field degrade honestly to "one key, or
     empty for any". */
  function asList(value) {
    if (value === null || value === undefined) return [];
    if (Object.prototype.toString.call(value) === '[object Array]') return value;
    var one = String(value);
    return one === '' ? [] : [one];
  }

  /* Answer keys are typed by a merchant into a metaobject and by a merchant into
     the console's option lines. Two people, two text boxes, one string that has
     to agree exactly — and the ways it can silently disagree are invisible in
     both UIs: a trailing space, a capital, a non-breaking space pasted out of a
     document, a zero-width character riding along from a copy. Comparing the
     normalised forms costs nothing and removes the whole class. The raw values
     are still what gets logged, so a genuine mismatch stays legible. */
  function normalise(value) {
    return String(value)
      .replace(/[\u00a0\u200b-\u200d\ufeff]/g, '')
      .trim()
      .toLowerCase();
  }

  function accepts(list, answer) {
    var wanted = normalise(answer);
    for (var i = 0; i < list.length; i++) {
      if (normalise(list[i]) === wanted) return true;
    }
    return false;
  }

  /* Per-rule breakdown, printed when nothing matched. This is the one moment
     where the raw values matter more than the verdict. */
  function explain(rule, answers) {
    var parts = [];
    for (var i = 0; i < MATCH_KEYS.length; i++) {
      var key = MATCH_KEYS[i];
      var raw = rule.match ? rule.match[key] : null;
      var list = asList(raw);
      var answer = answers[key] ? answers[key].value : '(unanswered)';
      var verdict = !list.length ? 'any' : (accepts(list, answer) ? 'ok' : 'FAILS');
      parts.push(key + ' ' + JSON.stringify(raw) + ' vs ' + JSON.stringify(answer) + ' -> ' + verdict);
    }
    return parts.join('  |  ');
  }

  /* Returns the rule's specificity, or -1 when it is eliminated. */
  function score(rule, answers) {
    var specificity = 0;

    for (var i = 0; i < MATCH_KEYS.length; i++) {
      var key = MATCH_KEYS[i];
      var accepted = asList(rule.match ? rule.match[key] : null);

      /* null (the field was never filled in) and [] both mean "any". */
      if (!accepted.length) continue;

      var answer = answers[key];
      if (!answer) return -1;
      if (!accepts(accepted, answer.value)) return -1;

      specificity++;
    }

    return specificity;
  }

  function bestRule(rules, answers) {
    var winner = null;
    var winnerScore = -1;

    for (var i = 0; i < rules.length; i++) {
      var s = score(rules[i], answers);
      if (s === -1) continue;

      if (s > winnerScore) {
        winner = rules[i];
        winnerScore = s;
        continue;
      }

      /* Equal specificity: priority decides, and if that ties too the entry that
         came first stays put — a stable result beats an arbitrary one. */
      if (s === winnerScore && winner && (rules[i].priority || 0) > (winner.priority || 0)) {
        winner = rules[i];
      }
    }

    return winner;
  }

  /* ---------------------------------------------------------------------
     Section
     --------------------------------------------------------------------- */

  function Result(root) {
    this.root = root;
    this.cards = root.querySelector(SEL.cards);
    this.alts = root.querySelector(SEL.alts);
    this.altsGrid = root.querySelector(SEL.altsGrid);
    this.altTemplate = root.querySelector(SEL.altTemplate);
    this.empty = root.querySelector(SEL.empty);
    this.live = root.querySelector(SEL.live);
    this.template = root.querySelector(SEL.template);

    this.ctaLabel = root.getAttribute('data-gf-cta-label') || 'See This Gift';
    this.soldOutLabel = root.getAttribute('data-gf-sold-out-label') || this.ctaLabel;
    this.moreLabel = root.getAttribute('data-gf-more-label') || '';
    this.noMatch = root.getAttribute('data-gf-no-match-message') || '';
    this.announcement = root.getAttribute('data-gf-announcement') || '';

    /* The band's own copy, kept so a no-match message can be swapped in and then
       swapped back out — otherwise one dead end would leave the "answer the
       three steps above" prompt permanently rewritten. */
    this.emptyMessage = this.empty ? this.empty.textContent : '';

    this.rules = this.readRules();
    try { this.catalog = JSON.parse(root.querySelector('[data-gf-finder-catalog]').textContent); } catch (error) { this.catalog = []; }
    this.corporatePanel = root.querySelector('[data-gf-result-corporate]');
    this.browse = root.querySelector('[data-gf-result-browse]');
    this.fallbackNote = root.querySelector('[data-gf-result-fallback]');
    this.lastRuleId = null;

    /* Counts straight off the Liquid, so a console message can name the stage
       that failed rather than saying "no match" for three different causes. */
    this.entryCount = parseInt(root.getAttribute('data-gf-entry-count'), 10) || 0;
    this.ruleCount = parseInt(root.getAttribute('data-gf-rule-count'), 10) || 0;
    this.type = root.getAttribute('data-gf-metaobject-type') || 'gift_finder_match';
    this.fieldAccess = root.getAttribute('data-gf-field-access') || 'none';

    var store = window.GFFinder;
    if (!store) {
      warn('window.GFFinder is missing — assets/gf-finder.js did not run, so no answers can reach this band.');
      return;
    }
    if (!this.cards || !this.template) {
      warn('The result markup is incomplete; the card template or its container is missing.');
      return;
    }

    this.reportRuleSupply();

    var self = this;
    store.subscribe(function (state) {
      self.onState(state);
    });
  }

  /* Said once on load, because a band with no rules behind it can never show a
     match and the reason is knowable before anyone touches a radio. */
  Result.prototype.reportRuleSupply = function () {
    if (this.rules.length) return;

    if (this.entryCount === 0) {
      warn(
        'No `' + this.type + '` entries reached the storefront — Liquid saw zero, so ' +
        'this is not a matching problem. Check, in order: the entries\' Status is Active, ' +
        'not Draft; storefront read access is on for the metaobject definition; and the ' +
        'type handle is exactly `' + this.type + '`.'
      );
    } else {
      warn(
        'Found ' + this.entryCount + ' `' + this.type + '` entries but none has a Product ' +
        'picked, so every one was skipped. A rule with no product has nothing to recommend.'
      );
    }
  };

  Result.prototype.readRules = function () {
    var node = this.root.querySelector(SEL.rules);
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (err) {
      /* A malformed rule set is a merchant-data problem, not a reason to take
         the page down. Log it and behave as if there were no rules. */
      if (window.console && window.console.error) {
        window.console.error('[gf-finder] Could not read the gift_finder_match rules.', err);
      }
      return [];
    }
  };

  Result.prototype.onState = function (state) {
    /* Only a deliberate "See Their Gift" reveals a match. Answering step 2 must
       not push a half-considered product onto the screen. */
    if (!state.submitted || !state.complete) {
      this.showEmpty(null);
      debug('waiting', { submitted: state.submitted, complete: state.complete, answers: state.answers });
      return;
    }

    var matching = window.GFFinderMatching;
    if (!matching) { this.showEmpty(this.noMatch); return; }
    if (this.corporatePanel) this.corporatePanel.hidden = true;
    if (this.browse) this.browse.hidden = true;
    if (this.fallbackNote) this.fallbackNote.hidden = true;
    var resultSubheading = this.root.querySelector('.gf-finder-result__sub');
    if (resultSubheading) resultSubheading.hidden = false;
    var resultHead = this.root.querySelector('.gf-finder-result__head');
    if (resultHead) resultHead.hidden = matching.corporate(state.answers);
    if (matching.corporate(state.answers)) {
      this.showEmpty('');
      if (this.empty) this.empty.hidden = true;
      this.corporatePanel.hidden = false;
      this.corporatePanel.querySelector('[data-gf-corporate-link]').href = '/pages/corporate-gifting#get-started';
      if (this.live) this.live.textContent = 'Start a gift inquiry. Our gifting team will help with your selection.';
      return;
    }
    var validRules = this.rules.filter(function (candidate) { return matching.eligible(candidate.card, state.answers); });
    var rule = bestRule(validRules, state.answers);
    if (rule) {
      rule = Object.assign({}, rule, {alternates: (rule.alternates || []).filter(function (card) { return matching.eligible(card, state.answers); })});
    } else {
      var gifts = matching.fallback(this.catalog, state.answers);
      if (gifts.length) {
        rule = {id: 'catalog-' + gifts[0].url, card: gifts[0], alternates: gifts.slice(1, 5)};
        if (this.fallbackNote) this.fallbackNote.hidden = false;
        if (resultSubheading) resultSubheading.hidden = true;
      }
    }
    if (!rule) {
      this.showEmpty(this.noMatch);
      if (this.browse) this.browse.hidden = false;
      this.lastRuleId = null;
      return;
    }

    debug('matched', rule.id, summarise(state.answers));
    this.render(rule, state);
  };

  Result.prototype.showEmpty = function (message) {
    if (this.corporatePanel) this.corporatePanel.hidden = true;
    if (this.fallbackNote) this.fallbackNote.hidden = true;
    if (this.browse) this.browse.hidden = true;
    if (this.empty) {
      this.empty.textContent = message || this.emptyMessage;
      this.empty.hidden = false;
    }
    this.cards.hidden = true;
    this.cards.textContent = '';
    if (this.alts) {
      this.alts.hidden = true;
      if (this.altsGrid) this.altsGrid.textContent = '';
    }
    if (this.live && message) this.live.textContent = message;
  };

  Result.prototype.picksLabel = function (state) {
    var parts = [];
    for (var i = 0; i < state.questions.length; i++) {
      var answer = state.answers[state.questions[i].key];
      if (answer) parts.push(answer.label);
    }
    return parts.join(' · ');
  };

  Result.prototype.render = function (rule, state) {
    var picks = this.picksLabel(state);

    this.cards.textContent = '';
    this.cards.hidden = false;
    if (this.empty) this.empty.hidden = true;

    /* A new match retires the previous match's alternates. Leaving them up would
       show gifts chosen for answers the visitor has already changed. */
    if (this.alts) {
      this.alts.hidden = true;
      if (this.altsGrid) this.altsGrid.textContent = '';
    }

    var primary = this.buildCard(rule.card, { picks: picks, alternates: rule.alternates || [] });
    this.cards.appendChild(primary);

    if (this.live && this.announcement) {
      this.live.textContent = this.announcement.replace('[title]', rule.card.title);
    }

    /* Park focus on the band so a keyboard or screen-reader visitor lands on the
       answer rather than staying at the button they pressed. The scroll is the
       console's job — doing both here would fight it. */
    if (state.reason === 'submit' && this.root.focus) {
      try {
        this.root.focus({ preventScroll: true });
      } catch (err) {
        this.root.focus();
      }
    }
    this.lastRuleId = rule.id;
  };

  Result.prototype.buildCard = function (card, options) {
    var opts = options || {};
    var node = this.template.content.cloneNode(true).firstElementChild;

    var media = node.querySelector('[data-gf-card-media]');
    var image = node.querySelector('[data-gf-card-image]');

    if (media) media.setAttribute('href', card.url || '#');

    if (image) {
      if (card.image) {
        image.setAttribute('src', card.image);
        if (card.imageSrcset) {
          image.setAttribute('srcset', card.imageSrcset);
          image.setAttribute('sizes', '(max-width: 749px) 100vw, 600px');
        }
        image.setAttribute('alt', card.imageAlt || '');
      } else {
        /* No image on the product and none on the rule: drop the media column
           rather than leave a grey box where a photograph should be. */
        if (media && media.parentNode) media.parentNode.removeChild(media);
      }
    }

    fill(node, 'badge', card.badge);
    fill(node, 'picks', opts.picks || '');
    fill(node, 'price', card.price);
    fill(node, 'meta', card.meta);
    fill(node, 'desc', card.description);

    var title = node.querySelector('[data-gf-card-title]');
    if (title) {
      title.textContent = card.title || '';
      title.setAttribute('href', card.url || '#');
    }

    var cta = node.querySelector('[data-gf-card-cta]');
    if (cta) {
      cta.textContent = card.available === false ? this.soldOutLabel : this.ctaLabel;
      cta.setAttribute('href', card.url || '#');
    }

    this.wireAlternates(node, opts.alternates || []);

    return node;
  };

  Result.prototype.wireAlternates = function (node, alternates) {
    var more = node.querySelector('[data-gf-card-more]');
    if (!more) return;

    if (!alternates.length || !this.moreLabel) {
      more.hidden = true;
      return;
    }

    more.hidden = false;
    more.textContent = this.moreLabel.replace('[count]', String(alternates.length));
    more.setAttribute('aria-expanded', 'false');

    var self = this;
    more.addEventListener('click', function () {
      /* One-way: alternates open and stay open. Collapsing a list the visitor
         asked to see, under a button that has scrolled off, is a trap. */
      more.hidden = true;
      self.showAlternates(alternates);
    });
  };

  /* The alternates are a 4-up grid of vertical cards in their own labelled
     region — not more of the primary's full-width horizontal card. Four of
     those stacked would bury the one match the page exists to make. */
  Result.prototype.showAlternates = function (alternates) {
    if (!this.alts || !this.altsGrid || !this.altTemplate) return;

    this.altsGrid.textContent = '';
    for (var i = 0; i < alternates.length; i++) {
      this.altsGrid.appendChild(this.buildAltCard(alternates[i]));
    }

    this.alts.hidden = false;

    /* Focus the region that just appeared, not the first card — a screen reader
       then announces the heading and how many cards are in it, instead of
       dropping the visitor into card one with no context. */
    try {
      this.alts.focus({ preventScroll: true });
    } catch (err) {
      this.alts.focus();
    }
    window.GFFinder.scrollTo(this.alts);
  };

  Result.prototype.buildAltCard = function (card) {
    var node = this.altTemplate.content.cloneNode(true).firstElementChild;

    var media = node.querySelector('[data-gf-card-media]');
    var image = node.querySelector('[data-gf-card-image]');

    if (media) media.setAttribute('href', card.url || '#');

    if (image && card.image) {
      image.setAttribute('src', card.image);
      if (card.imageSrcset) {
        image.setAttribute('srcset', card.imageSrcset);
        image.setAttribute('sizes', '(max-width: 749px) 78vw, (max-width: 989px) 45vw, 300px');
      }
      /* The media link is aria-hidden and out of the tab order — the title link
         below carries the name — so the image is decorative here. */
      image.setAttribute('alt', '');
    } else if (media && media.parentNode) {
      media.parentNode.removeChild(media);
    }

    var title = node.querySelector('[data-gf-card-title]');
    if (title) {
      title.textContent = card.title || '';
      title.setAttribute('href', card.url || '#');
    }

    fill(node, 'price', card.price);

    var cta = node.querySelector('[data-gf-card-cta-text]');
    if (cta) cta.textContent = card.available === false ? this.soldOutLabel : this.ctaLabel;

    return node;
  };

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  function init(scope) {
    each((scope || document).querySelectorAll(SEL.root), function (root) {
      if (root.gfFinderResult) return;
      root.gfFinderResult = new Result(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})(window, document);
